import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { logActivity } from '../services/admin/activityService';

/**
 * Logs a "page viewed" activity event on every authenticated route change so
 * the Management → Audit Log page reflects navigation — including pages that
 * make no backend API call (pure client-side routing).
 *
 * Mounted once, inside the authenticated AppLayout. Debounced and de-duplicated
 * so redirect chains / rapid nav don't spam the audit trail.
 */

// First route segment → product area (matches the audit "Service" filter).
const SERVICE_BY_SEGMENT = {
  admin: 'management',
  management: 'management',
  users: 'management',
  teams: 'management',
  security: 'security',
  finops: 'finops',
  compliance: 'compliance',
  gemini: 'gemini',
  copilot: 'copilot',
  sast: 'sast',
  'red-team-scanner': 'redteam',
  'red-team-scanner-old': 'redteam',
  'agent-atlas': 'monitoring',
  cloud: 'monitoring',
  'model-evaluation': 'evaluation',
  'saas-ai': 'openai',
};

// Auth/public/marketing routes we never record.
const SKIP_PREFIXES = ['/login', '/signup', '/register', '/claim', '/invite', '/reset', '/forgot', '/welcome', '/landing'];

const deriveService = (pathname) => {
  const seg = pathname.split('/').filter(Boolean)[0];
  return SERVICE_BY_SEGMENT[seg] || 'platform';
};

const shouldSkip = (pathname) => {
  if (!pathname || pathname === '/') return true;
  return SKIP_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
};

export default function useActivityTracking() {
  const location = useLocation();
  const lastPathRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const pathname = location.pathname;
    if (shouldSkip(pathname) || pathname === lastPathRef.current) return undefined;

    // Debounce so redirect chains collapse into the final destination.
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      lastPathRef.current = pathname;
      logActivity({
        action: 'ACCESS',
        method: 'VIEW',
        resourceType: 'page',
        resourceId: pathname,
        path: pathname + (location.search || ''),
        service: deriveService(pathname),
        statusCode: 200,
      });
    }, 600);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [location.pathname, location.search]);
}
